/**
 * Button on/off runtime - a shared, per-ButtonItem-id mirror of the lit
 * state `FireButton.tsx` already computes for itself (echo-corrected toggle
 * state). Exists so a DisplayItem bound to `source: 'button'` can read
 * another item's lit state without owning it - same purpose and shape as
 * `timeline/runtime.ts` (which lifted TimelineLauncher's private sim state
 * for the identical reason) and `sequencer/runtime.ts`/`macros/runtime.ts`.
 *
 * Momentary/trigger buttons hide their OWN on/off readout on their own face
 * (`FireButton.tsx`'s `showState()` - "too brief to be useful"), but this
 * store mirrors the raw signal regardless of mode: a bound Display simply
 * shows whatever the button is doing right now, including a brief blip for
 * momentary/trigger. That is a real, honest signal, not a bug to hide.
 */

import { createSignal, type Accessor } from 'solid-js';

const [runtimeSignal, setRuntimeSignal] = createSignal<Record<string, boolean>>({});

export const buttonRuntimeStates: Accessor<Record<string, boolean>> = runtimeSignal;

/** Called by FireButton.tsx's own additive effect on every `on()` change. */
export function recordButtonRuntime(itemId: string, on: boolean): void {
  setRuntimeSignal((prev) => (prev[itemId] === on ? prev : { ...prev, [itemId]: on }));
}

/** Called by FireButton.tsx on unmount so a removed button doesn't leave a
 *  bound Display reading a stale ghost value. */
export function clearButtonRuntime(itemId: string): void {
  setRuntimeSignal((prev) => {
    if (!(itemId in prev)) return prev;
    const next = { ...prev };
    delete next[itemId];
    return next;
  });
}
